import React, { FC, ReactNode, useRef } from "react"
import { motion, AnimatePresence } from "motion/react"

import { createPortal } from "react-dom"
import cls from "classnames"

import styles from "./index.module.scss"
import { i18n } from "@/i18n"
import OutlineButton from "../OutlineButton"
import { sfxPlay } from "@/assets/audios/sfx"

type OutlineButtonProps = React.ComponentProps<typeof OutlineButton>

interface ModalProps {
    open: boolean
    onClose: () => void
    onConfirm?: () => void
    confirmText?: string
    children: ReactNode
    maskClosable?: boolean
    maskClass?: string
    showConfirmBtn?: boolean
    showCloseBtn?: boolean
    modalClass?: string
    contentClass?: string
    confirmBtnClass?: string
    confirmBtnProps?: Omit<OutlineButtonProps, "text" | "onClick" | "className">
    autoClose?: boolean
    pure?: boolean
}

export const Modal: FC<ModalProps> = ({
    open,
    onClose,
    onConfirm,
    confirmText,
    children,
    maskClosable = true,
    maskClass,
    showConfirmBtn = true,
    showCloseBtn = true,
    modalClass,
    contentClass,
    confirmBtnClass,
    confirmBtnProps,
    autoClose = true,
    pure = false,
}) => {
    const showingText = confirmText || i18n.get("common.yes")
    const maskRef = useRef<HTMLDivElement | null>(null)

    const handleMaskClick = () => {
        if (maskClosable) {
            onClose()
        }
    }

    const handleConfirm = () => {
        sfxPlay("apply_click")
        onConfirm?.()
        if (autoClose) {
            onClose()
        }
    }

    const handleCloseClick = () => {
        sfxPlay("back_click")
        onClose()
    }

    // 使用 Portal 渲染到 body 顶层，避免受父组件堆叠上下文影响
    return typeof document !== "undefined"
        ? createPortal(
              <AnimatePresence>
                  {open && (
                      <motion.div
                          key="modal-mask"
                          ref={maskRef}
                          className={cls(styles.mask, maskClass)}
                          onClick={handleMaskClick}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{
                              duration: 0.4,
                              ease: "easeInOut",
                          }}
                      >
                          {pure ? (
                              <motion.div
                                  className={cls(styles.pureModal, modalClass)}
                                  onClick={(e) => e.stopPropagation()}
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.8 }}
                                  transition={{
                                      duration: 0.3,
                                      ease: "easeInOut",
                                  }}
                              >
                                  {children}
                              </motion.div>
                          ) : (
                              <motion.div
                                  className={cls(styles.modal, modalClass)}
                                  onClick={(e) => e.stopPropagation()}
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.8 }}
                                  transition={{
                                      duration: 0.3,
                                      ease: "easeInOut",
                                  }}
                              >
                                  {showCloseBtn && (
                                      <img
                                          src={require("@/assets/imgs/common/close.png")}
                                          alt="close"
                                          className={styles.closeBtn}
                                          data-close-btn
                                          onClick={handleCloseClick}
                                      ></img>
                                  )}
                                  <div
                                      className={cls(
                                          styles.content,
                                          contentClass
                                      )}
                                  >
                                      {children}
                                  </div>
                                  {showConfirmBtn && (
                                      <OutlineButton
                                          text={showingText}
                                          className={cls(
                                              styles.confirmBtn,
                                              confirmBtnClass
                                          )}
                                          noSound
                                          onClick={handleConfirm}
                                          strokeColor="#882F19"
                                          strokeWidth={8}
                                          fontSize={40}
                                          fillGradient={{
                                              from: "#FFFAF6",
                                              to: "#FCF1D2",
                                          }}
                                          {...confirmBtnProps}
                                      ></OutlineButton>
                                  )}
                              </motion.div>
                          )}
                      </motion.div>
                  )}
              </AnimatePresence>,
              document.body
          )
        : null
}
